import { useContext } from 'react';
import { Box, Typography, Stack, Button, IconButton } from '@mui/joy';
import { Clock, Lightning } from '@phosphor-icons/react';

import { LayoutContext } from '../../context';

// Персистентный баннер триала. Виден поверх контента на всех авторизованных
// страницах, пока state === 'active_trial'. Для expired state — другой
// компонент (PaywallScreen) показывается целиком.
//
// Правило из фидбека ("никогда не оставлять empty state"): всегда
// ведём к CTA — клик по баннеру открывает UpgradeModal.
export default function TrialBanner() {
    const { trialInfo, openUpgradeModal, user } = useContext(LayoutContext);

    // Не показывать: админу, платнику, без триала, или если данные ещё не
    // загружены.
    if (!user || user.role === 'admin') return null;
    if (!trialInfo) return null;
    if (trialInfo.state === 'paid') return null;
    // Платным (оплачен любой инвойс, в т.ч. ждущий активации) — баннер не показываем.
    if (trialInfo.has_paid_invoice) return null;

    // tg_promo (закрытый Telegram-класс): persistent CTA на апгрейд по цене класса.
    // У них уже открыты БХ/Иммуно/Кардио, но остальной курс — за 1590₽/3990₽/6990₽
    // (см. TG_PROMO_PLANS в UpgradeModal.jsx). Показываем независимо от trial state.
    const isTgPromo = trialInfo.is_tg_promo === true;
    if (isTgPromo) {
        return (
            <Box
                sx={{
                    position: 'sticky', top: 0, zIndex: 900, width: '100%',
                    background: 'linear-gradient(90deg, #B84C31 0%, #DA7756 100%)',
                    color: '#FFFFFF',
                    borderBottom: '1px solid', borderColor: 'rgba(255,255,255,0.1)',
                    px: { xs: 1.5, sm: 2.5 }, py: { xs: 0.75, sm: 1 },
                }}
            >
                <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }} sx={{ maxWidth: 1400, mx: 'auto' }}>
                    <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Lightning size={16} weight="fill" color="#FFFFFF" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="body-sm" sx={{
                            color: 'inherit', fontWeight: 600, lineHeight: 1.3,
                            fontSize: { xs: 12, sm: 14 },
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        }}>
                            Открыты БХ, Иммуно, Кардио · открой весь курс по цене класса от 1 590 ₽
                        </Typography>
                    </Box>
                    <Button
                        size="sm"
                        variant="solid"
                        onClick={() => openUpgradeModal('Открой весь курс APEX по цене закрытого Telegram-класса.')}
                        sx={{
                            bgcolor: '#1F1F1F', color: '#FFFFFF',
                            fontWeight: 700, fontSize: { xs: 11, sm: 13 },
                            px: { xs: 1.25, sm: 2 }, py: 0.5, minHeight: 0, flexShrink: 0,
                            '&:hover': { bgcolor: '#37352F', color: '#FFFFFF' },
                        }}
                    >
                        Улучшить тариф
                    </Button>
                </Stack>
            </Box>
        );
    }

    if (trialInfo.state !== 'active_trial') return null;

    const days = trialInfo.days_left ?? 0;
    const daysLabel = days === 1 ? 'день' : days >= 2 && days <= 4 ? 'дня' : 'дней';
    const isCritical = days <= 1;

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 900,
                width: '100%',
                bgcolor: 'linear-gradient(90deg, #B84C31 0%, #DA7756 100%)',
                background: 'linear-gradient(90deg, #B84C31 0%, #DA7756 100%)',
                color: '#FFFFFF',
                borderBottom: '1px solid',
                borderColor: 'rgba(255,255,255,0.1)',
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 0.75, sm: 1 },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 1.5 }}
                sx={{ maxWidth: 1400, mx: 'auto' }}
            >
                {/* Icon */}
                <Box sx={{
                    width: 28, height: 28, borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {isCritical
                        ? <Lightning size={16} weight="fill" color="#FFFFFF" />
                        : <Clock size={16} weight="fill" color="#FFFFFF" />}
                </Box>

                {/* Message */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        level="body-sm"
                        sx={{
                            color: 'inherit',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            fontSize: { xs: 12, sm: 14 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: { xs: 'nowrap', sm: 'normal' },
                        }}
                    >
                        {isCritical && days === 0
                            ? 'Сегодня последний день триала'
                            : `Триал: осталось ${days} ${daysLabel}`}
                        <Box component="span" sx={{
                            ml: 1,
                            opacity: 0.8,
                            fontWeight: 400,
                            display: { xs: 'none', md: 'inline' },
                        }}>
                            · открыт день 1 Биохимии и Иммунологии, 20 мини-тестов, 10 задач
                        </Box>
                    </Typography>
                </Box>

                {/* CTA */}
                <Button
                    size="sm"
                    variant="solid"
                    onClick={() => openUpgradeModal('Оформи полный доступ, чтобы открыть весь курс.')}
                    sx={{
                        bgcolor: '#1F1F1F',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: { xs: 11, sm: 13 },
                        px: { xs: 1.25, sm: 2 },
                        py: 0.5,
                        minHeight: 0,
                        flexShrink: 0,
                        '&:hover': {
                            bgcolor: '#37352F',
                            color: '#FFFFFF',
                        },
                    }}
                >
                    {isCritical ? 'Продлить доступ' : 'Открыть всё'}
                </Button>
            </Stack>
        </Box>
    );
}
